<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlitable mb-10"
      :items="items"
      :headers="headers"
      :loading="loading"
      :fixed-header="true"
      :show-select="false"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      item-value="id"
      @update:options="handlePageChange"
    >
      <template #item="{ item, index }">
        <tr
          :id="`${index + 1}`"
          :key="index"
          :class="{ 'row-disabled': isRowDisabled(item.disabled) || shouldDisableBlocklist(item) }"
        >
          <td class="pl-2 pr-0">
            <v-icon
              :class="{ 'text-indigo-medium': item.disabled || shouldDisableBlocklist(item) }"
              :size="43"
              :icon="iconToTypeMap(item.category)"
            />
            <span
              class="body2 text--semibold"
              :class="{ 'text-indigo-medium': item.disabled || shouldDisableBlocklist(item) }"
              v-text="item.name"
            />
          </td>
          <td>
            <span
              v-if="item.allowed"
              class="body2"
              :class="{
                'text-green-dark': !item.disabled,
                'text-indigo-medium': item.disabled || shouldDisableBlocklist(item),
              }"
              v-text="$t('network.secureWebGateway.table.allowed')"
            />
            <span
              v-else
              class="body2"
              :class="{
                'text-red-dark': !item.disabled && !shouldDisableBlocklist(item),
                'text-indigo-medium': item.disabled || shouldDisableBlocklist(item),
              }"
              v-text="$t('network.secureWebGateway.table.blocked')"
            />
          </td>
          <td>
            <span
              class="body2"
              :class="{ 'text-indigo-medium': item.disabled || shouldDisableBlocklist(item) }"
              v-text="$t(`network.secureWebGateway.filters.${item.category}`)"
            />
          </td>
          <td>
            <div class="d-flex align-center">
              <v-chip
                v-for="label in item.labels.slice(0, MAX_VISIBLE_LABELS)"
                :key="label.name"
                size="default"
                :color="label.color"
                close-icon="$close"
                class="label-chip mr-1"
                variant="flat"
                label
                rounded="3"
              >
                <span class="coro-label text-uppercase text-primary mr-1" v-text="label.name">
                </span>
              </v-chip>
              <v-tooltip
                v-if="item.labels.length > MAX_VISIBLE_LABELS"
                location="end"
                content-class="labels-tooltip"
              >
                <template #activator="{ props }">
                  <v-chip
                    v-bind="props"
                    size="default"
                    color="indigo-pale"
                    class="label-chip overflow-chip"
                    variant="flat"
                    label
                    rounded="3"
                  >
                    <span class="coro-label text-uppercase text-primary">
                      +{{ item.labels.length - MAX_VISIBLE_LABELS }}
                    </span>
                  </v-chip>
                </template>
                <div class="d-flex flex-column gap-1">
                  <div
                    v-for="label in item.labels.slice(MAX_VISIBLE_LABELS)"
                    :key="label.name"
                    class="body2 text-white"
                  >
                    {{ label.name }}
                  </div>
                </div>
              </v-tooltip>
            </div>
          </td>
          <td class="d-flex justify-end align-center coro-table-col--sticky-right">
            <v-menu location="left" :disabled="loading">
              <template #activator="{ props }">
                <v-icon v-bind="props" icon="$dots" :data-testid="`swg-menu-btn-${index}`" />
              </template>
              <v-list data-testid="swg-action-list">
                <template v-for="(action, actionIndex) in getActionsList(item)" :key="actionIndex">
                  <v-list-item
                    :data-testid="`swg-action-item-${actionIndex}`"
                    @click="handleItemAction(action, item)"
                    :disabled="shouldDisableBlockAction(action, item)"
                  >
                    <span v-text="$t(`network.secureWebGateway.actions.${action}`)" />
                    <template v-if="shouldDisableBlockAction(action, item)" #append>
                      <span class="pointer-events-auto">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <v-icon
                              @click.native.prevent.stop.capture
                              v-bind="props"
                              color="grey-lighten-1"
                              icon="$info"
                            >
                            </v-icon>
                          </template>
                          <div
                            class="text-center w-100"
                            v-html="$t('network.secureWebGateway.disabledBlocklistTooltip')"
                          ></div>
                        </v-tooltip>
                      </span>
                    </template>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
      <template #no-data>
        <div class="my-12 d-flex flex-column align-center">
          <v-icon
            class="mb-1"
            :icon="isFiltered ? '$fullDetailsL' : '$fileUploadL'"
            color="indigo-light"
            size="80"
          ></v-icon>
          <div class="headline6 mb-1">
            {{
              isFiltered
                ? $t("network.secureWebGateway.noResultsTitle")
                : $t("network.secureWebGateway.emptyTableTitle")
            }}
          </div>
          <div class="body-2 empty-data-state">
            {{
              isFiltered
                ? $t("network.secureWebGateway.noResultsSubTitle")
                : $t("network.secureWebGateway.emptyTableSubTitle")
            }}
          </div>
        </div>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import TableWrapper from "@/components/TableWrapper.vue";
import { SecureWebGatewayAction, SecureWebGatewayCategories } from "@/constants/network";
import type { Pagination } from "@/types";
import type { SecureWebGatewayUrlItem } from "@/_store/network/swg/secure-web-gateway.module.ts";
import { useI18n } from "vue-i18n";

const MAX_VISIBLE_LABELS = 3;

export default defineComponent({
  components: {
    TableWrapper,
  },
  props: {
    items: { type: Array as PropType<SecureWebGatewayUrlItem[]>, required: true },
    dnsAllowlistFilteringEnabled: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    totalItems: { type: Number, required: true },
    pagination: { type: Object as PropType<Pagination>, required: true },
    isFiltered: { type: Boolean, default: false },
  },
  emits: ["page-change", "item-action-applied"],

  setup(props, { emit }) {
    const { t } = useI18n();

    const handleItemAction = (
      action: SecureWebGatewayAction,
      item: SecureWebGatewayUrlItem
    ): void => {
      emit("item-action-applied", { action, item });
    };

    const handlePageChange = (payload: Pagination): void => {
      emit("page-change", payload);
    };

    const getActionsList = (item: SecureWebGatewayUrlItem) => {
      const enableAction = item.allowed
        ? SecureWebGatewayAction.ENABLE_URL_GROUP_ALLOW_LIST
        : SecureWebGatewayAction.ENABLE_URL_GROUP_BLOCK_LIST;

      const enableUrlAction =
        item.allowed && item.category === SecureWebGatewayCategories.URL
          ? SecureWebGatewayAction.ENABLE_URL_ALLOW_LIST
          : SecureWebGatewayAction.ENABLE_URL_BLOCK_LIST;

      const disableAction = item.allowed
        ? SecureWebGatewayAction.DISABLE_URL_GROUP_ALLOW_LIST
        : SecureWebGatewayAction.DISABLE_URL_GROUP_BLOCK_LIST;

      const disableUrlAction =
        item.allowed && item.category === SecureWebGatewayCategories.URL
          ? SecureWebGatewayAction.DISABLE_URL_ALLOW_LIST
          : SecureWebGatewayAction.DISABLE_URL_BLOCK_LIST;

      const removeAction = item.allowed
        ? SecureWebGatewayAction.REMOVE_URL_GROUP_FROM_ALLOW_LIST
        : SecureWebGatewayAction.REMOVE_URL_GROUP_FROM_BLOCK_LIST;

      const removeUrlAction =
        item.allowed && item.category === SecureWebGatewayCategories.URL
          ? SecureWebGatewayAction.REMOVE_FROM_ALLOWLIST
          : SecureWebGatewayAction.REMOVE_FROM_BLOCKLIST;

      switch (item.category) {
        case SecureWebGatewayCategories.URL_GROUP:
          return [
            SecureWebGatewayAction.VIEW_URL_LIST,
            item.disabled ? enableAction : disableAction,
            SecureWebGatewayAction.EDIT_URL_GROUP,
            removeAction,
          ];
        case SecureWebGatewayCategories.LOCKED_URL_GROUP:
          return [
            SecureWebGatewayAction.VIEW_URL_LIST,
            SecureWebGatewayAction.EDIT_URL_GROUP,
            item.disabled ? enableAction : disableAction,
          ];
        default:
          return [
            SecureWebGatewayAction.EDIT_URL,
            item.disabled ? enableUrlAction : disableUrlAction,
            removeUrlAction,
          ];
      }
    };

    const iconToTypeMap = (type: string) => {
      const types: Record<string, string> = {
        urlGroup: "folder",
        lockedUrlGroup: "folderLocked",
        url: "globe",
      };
      return `$${types[type]}`;
    };

    const isRowDisabled = (disabled: boolean): boolean | null => {
      return disabled ? true : null;
    };

    const shouldDisableBlocklist = (item: SecureWebGatewayUrlItem): boolean => {
      return !item.allowed && props.dnsAllowlistFilteringEnabled;
    };

    const shouldDisableBlockAction = (
      action: SecureWebGatewayAction,
      item: SecureWebGatewayUrlItem
    ) => {
      const blockActions = [
        SecureWebGatewayAction.ENABLE_URL_GROUP_BLOCK_LIST,
        SecureWebGatewayAction.DISABLE_URL_GROUP_BLOCK_LIST,
        SecureWebGatewayAction.ENABLE_URL_BLOCK_LIST,
        SecureWebGatewayAction.DISABLE_URL_BLOCK_LIST,
      ];
      return blockActions.includes(action) && shouldDisableBlocklist(item);
    };

    const headers = [
      {
        title: t("network.secureWebGateway.table.name"),
        key: "name",
        sortable: false,
        width: "30%",
      },
      {
        title: t("network.secureWebGateway.table.status"),
        key: "status",
        sortable: false,
        width: "10%",
      },
      {
        title: t("network.secureWebGateway.table.category"),
        key: "category",
        sortable: false,
        width: "15%",
      },
      {
        title: t("network.secureWebGateway.labelsAutocompleteLabel"),
        key: "labels",
        sortable: false,
        width: "40%",
      },
      {
        width: "5%",
      },
    ];

    return {
      isRowDisabled,
      handleItemAction,
      handlePageChange,
      getActionsList,
      headers,
      SecureWebGatewayAction,
      iconToTypeMap,
      SecureWebGatewayCategories,
      shouldDisableBlocklist,
      shouldDisableBlockAction,
      MAX_VISIBLE_LABELS,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .text-indigo-medium {
    &.icon-folder-coro-s:before {
      color: rgb(var(--v-theme-indigo-medium));
    }
  }
  .text-indigo-light {
    &.icon-file-upload-l:before,
    &.icon-full-details-l:before {
      color: rgb(var(--v-theme-indigo-light));
    }
  }
}

.row-disabled {
  background-color: rgb(var(--v-theme-gray-faint));

  // action column is white by default, on a disabled row it must match the row's background
  .coro-table-col--sticky-right {
    background-color: rgb(var(--v-theme-gray-faint));
  }
}

.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

.overflow-chip {
  cursor: pointer;
}

.empty-data-state {
  color: rgb(var(--v-theme-indigo-medium));
}
</style>
